var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { differenceBy } from 'lodash';
import ADD_MODEL_FOLDER from 'api/mutations/ADD_MODEL_FOLDER';
import REMOVE_MODEL_SPECIFICATION_FOLDER from 'api/mutations/REMOVE_MODEL_SPECIFICATION_FOLDER';
import RENAME_SPECIFICATION_FOLDER from 'api/mutations/RENAME_SPECIFICATION_FOLDER';
import GET_CURRENT_TENANT from 'api/queries/GET_CURRENT_TENANT';
import GET_SPECIFICATION_FOLDER_CONTENTS_DATA from 'api/queries/GET_SPECIFICATION_FOLDER_CONTENTS_DATA';
import { SPECIFICATIONS_PAGE_SIZE } from 'constants/pagination';
import { splitStatusFilter } from 'utils/specificationStatusFilter';
import client from '../api/environment';
import { setSpecificationStateToProduction } from './specificationDetailsSlice';
const initialState = {
    filters: {
        search: '',
        tags: null,
        orderBy: '',
        orderName: '',
        othersOrderBy: '',
        statusFilter: [],
        categoryFilter: [],
    },
    foldersTree: [],
    rootFolder: {
        rootFolderId: null,
        status: 'idle',
        error: null,
    },
    parentFolder: null,
    folders: [],
    specifications: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    loadedFolderId: null,
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
    },
    error: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
    },
    refetchFlags: {
        fetch: false,
        fetchList: false,
    },
};
export const fetchRootFolder = createAsyncThunk('specificationsFolders/fetchRootFolder', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT,
            fetchPolicy: 'network-only',
        });
        return response.data.me.currentTenant.rootFolder.id;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchRootFolders = createAsyncThunk('specificationsFolders/fetchRootFolders', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ folderId }, { rejectWithValue }) {
    try {
        const response = yield client.query({
            query: GET_SPECIFICATION_FOLDER_CONTENTS_DATA,
            variables: {
                modelSpecificationFolderId: folderId,
                search: '',
                orderBy: '',
                orderName: '',
                othersOrderBy: '',
                tags: [],
                isActive: true,
                first: 0,
                after: null,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.node;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationsFolders = createAsyncThunk('specificationsFolders/fetchSpecificationsFolders', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ folderId, append = false, states = null }, { getState, rejectWithValue }) {
    const state = getState();
    const { filters, pageInfo } = state.specificationsFolders;
    const { state: statusFilterState, statusId: statusFilterStatusId } = states
        ? { state: states, statusId: null }
        : splitStatusFilter(filters.statusFilter);
    try {
        const response = yield client.query({
            query: GET_SPECIFICATION_FOLDER_CONTENTS_DATA,
            variables: {
                modelSpecificationFolderId: folderId,
                search: filters.search,
                orderBy: filters.orderBy,
                orderName: filters.orderName,
                othersOrderBy: filters.othersOrderBy,
                tags: [],
                isActive: true,
                first: SPECIFICATIONS_PAGE_SIZE,
                after: append ? pageInfo.endCursor : null,
                state: statusFilterState,
                statusId: statusFilterStatusId,
                categoryId: filters.categoryFilter.length > 0 ? filters.categoryFilter : null,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.node;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addFolder = createAsyncThunk('specificationsFolders/addFolder', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ name, parentFolderId }, { rejectWithValue }) {
    var _c, _d, _e, _f;
    try {
        const data = yield client.mutate({
            mutation: ADD_MODEL_FOLDER,
            variables: {
                name,
                parentFolderId,
            },
            fetchPolicy: 'no-cache',
        });
        return {
            folder: data.data.addModelSpecificationFolder.modelSpecificationFolder,
            parentFolderId,
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue((_f = (_e = (_d = (_c = apolloError.graphQLErrors) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.extensions) === null || _e === void 0 ? void 0 : _e.exceptionCode) !== null && _f !== void 0 ? _f : apolloError.message);
    }
}));
export const updateFolder = createAsyncThunk('specificationsFolders/updateFolder', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ name, modelSpecificationFolderId }, { rejectWithValue }) {
    var _c, _d, _e, _f;
    try {
        const data = yield client.mutate({
            mutation: RENAME_SPECIFICATION_FOLDER,
            variables: {
                name,
                modelSpecificationFolderId,
            },
            fetchPolicy: 'no-cache',
        });
        return data.data.updateModelSpecificationFolder.modelSpecificationFolder;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue((_f = (_e = (_d = (_c = apolloError.graphQLErrors) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.extensions) === null || _e === void 0 ? void 0 : _e.exceptionCode) !== null && _f !== void 0 ? _f : apolloError.message);
    }
}));
export const removeFolder = createAsyncThunk('specificationsFolders/removeFolder', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ folderId, parentFolderId }, { rejectWithValue }) {
    try {
        const data = yield client.mutate({
            mutation: REMOVE_MODEL_SPECIFICATION_FOLDER,
            variables: {
                folderId,
            },
            fetchPolicy: 'no-cache',
        });
        return {
            folderId,
            parentFolderId,
            data,
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const specificationsFoldersSlice = createSlice({
    name: 'specificationsFolders',
    initialState,
    reducers: {
        setFilter(state, action) {
            const { filterName, value } = action.payload;
            state.filters[filterName] = value;
        },
        toggleStatusFilter(state, action) {
            const value = action.payload;
            state.filters.statusFilter = state.filters.statusFilter.includes(value)
                ? state.filters.statusFilter.filter((item) => item !== value)
                : [...state.filters.statusFilter, value];
        },
        resetStatusFilter(state) {
            state.filters.statusFilter = [];
        },
        toggleCategoryFilter(state, action) {
            const value = action.payload;
            state.filters.categoryFilter = state.filters.categoryFilter.includes(value)
                ? state.filters.categoryFilter.filter((item) => item !== value)
                : [...state.filters.categoryFilter, value];
        },
        resetCategoryFilter(state) {
            state.filters.categoryFilter = [];
        },
        setRefetchFlag(state, action) {
            const { flag, value } = action.payload;
            state.refetchFlags[flag] = value;
        },
        resetFoldersTree(state) {
            state.foldersTree = [];
            state.refetchFlags.fetchList = true;
        },
        resetFilters(state) {
            state.filters = {
                search: '',
                tags: null,
                orderBy: '',
                orderName: '',
                othersOrderBy: '',
                statusFilter: [],
                categoryFilter: [],
            };
        },
        resetSpecificationsFolders(state) {
            state.parentFolder = null;
            state.folders = [];
            state.specifications = [];
            state.pageInfo = {
                hasNextPage: true,
                endCursor: null,
            };
            state.loadedFolderId = null;
            state.status.fetch = 'idle';
            state.error.fetch = null;
        },
        removeStoreFolderSpecification: (state, action) => {
            state.specifications = state.specifications.filter((specification) => specification.id !== action.payload);
        },
        toggleIsFolderOpened: (state, action) => {
            const { id } = action.payload;
            const itemIndex = state.foldersTree.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.foldersTree[itemIndex] = Object.assign(Object.assign({}, state.foldersTree[itemIndex]), { opened: !state.foldersTree[itemIndex].opened });
            }
        },
        addSpecFolderListPhoto(state, action) {
            const { id, image } = action.payload;
            const index = state.specifications.findIndex((s) => s.id === id);
            if (index !== -1) {
                state.specifications[index].image = image;
            }
        },
        removeSpecFolderListPhoto(state, action) {
            const { id } = action.payload;
            const index = state.specifications.findIndex((s) => s.id === id);
            if (index !== -1) {
                state.specifications[index].image = null;
            }
        },
        updateSpecFolderListStatus(state, action) {
            const { id, status } = action.payload;
            const index = state.specifications.findIndex((s) => s.id === id);
            if (index !== -1) {
                state.specifications[index].status = status;
            }
        },
        updateSpecFolderListState(state, action) {
            const { id, state: newState } = action.payload;
            const index = state.specifications.findIndex((s) => s.id === id);
            if (index !== -1) {
                state.specifications[index].state = newState;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRootFolder.pending, (state) => {
            state.rootFolder.status = 'loading';
        })
            .addCase(fetchRootFolder.fulfilled, (state, action) => {
            state.rootFolder.rootFolderId = action.payload;
            state.rootFolder.status = 'succeeded';
        })
            .addCase(fetchRootFolder.rejected, (state, action) => {
            state.rootFolder.status = 'failed';
            state.rootFolder.error = action.error.message || 'Error fetching root folder';
        })
            .addCase(fetchRootFolders.pending, (state) => {
            state.status.fetch = 'loading';
        })
            .addCase(fetchRootFolders.fulfilled, (state, action) => {
            const fetchedFolders = action.payload.folders.edges.map(({ node }) => {
                return {
                    id: node.id,
                    name: node.name,
                    foldersCount: node.foldersCount,
                    parentId: node.parentFolder.id,
                    opened: false,
                };
            });
            const newFolders = differenceBy(fetchedFolders, state.foldersTree, 'id');
            state.foldersTree = [...state.foldersTree, ...newFolders];
            state.status.fetch = 'succeeded';
            state.refetchFlags.fetchList = false;
        })
            .addCase(fetchRootFolders.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.error.fetch = action.error.message || 'Error fetching specifications folders';
        })
            .addCase(fetchSpecificationsFolders.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchSpecificationsFolders.fulfilled, (state, action) => {
            const fetchedFolders = action.payload.folders.edges.map(({ node }) => {
                return {
                    id: node.id,
                    name: node.name,
                    foldersCount: node.foldersCount,
                    modelSpecificationsCount: node.modelSpecificationsCount,
                    canBeRemoved: node.canBeRemoved,
                    parentId: node.parentFolder.id,
                    opened: false,
                };
            });
            const newFolders = differenceBy(fetchedFolders, state.foldersTree, 'id');
            state.foldersTree = [...state.foldersTree, ...newFolders];
            state.parentFolder = action.payload.parentFolder;
            state.folders = fetchedFolders;
            const fetchedSpecifications = action.payload.modelSpecifications.edges.map((node) => node.node);
            if (action.meta.arg.append) {
                const newSpecifications = fetchedSpecifications.filter((specification) => !state.specifications.some((existing) => existing.id === specification.id));
                state.specifications = [...state.specifications, ...newSpecifications];
            }
            else {
                state.specifications = fetchedSpecifications;
            }
            state.pageInfo = action.payload.modelSpecifications.pageInfo;
            state.loadedFolderId = action.meta.arg.folderId;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchSpecificationsFolders.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.error.fetch = action.error.message || 'Error fetching specifications folders';
        })
            .addCase(setSpecificationStateToProduction.fulfilled, (state, action) => {
            const { id, state: specificationState } = action.payload;
            const specification = state.specifications.find((item) => item.id === id);
            if (specification) {
                specification.state = specificationState;
            }
            state.refetchFlags.fetch = true;
        })
            .addCase(addFolder.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addFolder.fulfilled, (state, action) => {
            state.status.add = 'succeeded';
            const { folder, parentFolderId } = action.payload;
            const parent = state.foldersTree.find((f) => f.id === parentFolderId);
            state.folders = [Object.assign(Object.assign({}, folder), { parentId: parentFolderId }), ...state.folders];
            state.foldersTree = [Object.assign(Object.assign({}, folder), { parentId: parentFolderId }), ...state.foldersTree];
            if (parent) {
                parent.foldersCount = (parent.foldersCount || 0) + 1;
            }
        })
            .addCase(addFolder.rejected, (state, action) => {
            state.status.add = 'failed';
            state.error.add = action.error.message || 'Error fetching specifications folders';
        })
            .addCase(updateFolder.pending, (state) => {
            state.status.update = 'loading';
        })
            .addCase(updateFolder.fulfilled, (state, action) => {
            state.status.update = 'succeeded';
            const folder = action.payload;
            const foldersIndex = state.folders.findIndex((f) => f.id === folder.id);
            if (foldersIndex !== -1) {
                state.folders[foldersIndex] = Object.assign(Object.assign({}, state.foldersTree[foldersIndex]), folder);
            }
            const treeIndex = state.foldersTree.findIndex((f) => f.id === folder.id);
            if (treeIndex !== -1) {
                state.foldersTree[treeIndex] = Object.assign(Object.assign({}, state.foldersTree[treeIndex]), folder);
            }
        })
            .addCase(updateFolder.rejected, (state, action) => {
            state.status.update = 'failed';
            state.error.update = action.error.message || 'Error fetching specifications folders';
        })
            .addCase(removeFolder.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeFolder.fulfilled, (state, action) => {
            state.status.remove = 'succeeded';
            const { folderId, parentFolderId } = action.payload;
            const parent = state.foldersTree.find((f) => f.id === parentFolderId);
            state.folders = state.folders.filter((folder) => folder.id !== folderId);
            state.foldersTree = state.foldersTree.filter((folder) => folder.id !== folderId);
            if (parent) {
                parent.foldersCount -= 1;
            }
        })
            .addCase(removeFolder.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.error.remove = action.error.message || 'Error fetching specifications folders';
        });
    },
});
export const { setFilter, toggleStatusFilter, resetStatusFilter, toggleCategoryFilter, resetCategoryFilter, setRefetchFlag, resetFoldersTree, resetFilters, resetSpecificationsFolders, removeStoreFolderSpecification, toggleIsFolderOpened, addSpecFolderListPhoto, removeSpecFolderListPhoto, updateSpecFolderListStatus, updateSpecFolderListState, } = specificationsFoldersSlice.actions;
export const selectRootFolder = (state) => state.specificationsFolders.rootFolder;
export const selectParentFolder = (state) => state.specificationsFolders.parentFolder;
export const selectFoldersTree = (state) => state.specificationsFolders.foldersTree;
export const selectIsFolderOpened = (state, folderId) => {
    var _a;
    const foldersTree = (_a = state.specificationsFolders) === null || _a === void 0 ? void 0 : _a.foldersTree;
    if (!foldersTree || !Array.isArray(foldersTree)) {
        return false;
    }
    const folder = foldersTree.find((folder) => folder.id === folderId);
    return folder === null || folder === void 0 ? void 0 : folder.opened;
};
export const selectFolderName = (state, folderId) => {
    var _a, _b;
    return ((_b = (_a = state.specificationsFolders.foldersTree) === null || _a === void 0 ? void 0 : _a.find((folder) => folder.id === folderId)) === null || _b === void 0 ? void 0 : _b.name) ||
        '';
};
export const selectFoldersSpecifications = (state) => state.specificationsFolders.specifications;
export const selectFilters = (state) => state.specificationsFolders.filters;
export const selectStatus = (state) => { var _a; return (_a = state === null || state === void 0 ? void 0 : state.specificationsFolders) === null || _a === void 0 ? void 0 : _a.status; };
export default specificationsFoldersSlice.reducer;
